/**
 * Utility for grouping public disk images by base name.
 * E.g., "python-3.11" -> group "python", version "3.11"
 *
 * @module utils/diskImageGrouping
 */

export interface DiskImageVersion {
  value: string;
  label: string;
}

export interface DiskImageGroup {
  key: string;
  label: string;
  versions: DiskImageVersion[];
}

/** Regex to split image name into base name and version. */
const VERSION_REGEX = /^(.+?)-(\d.*)$/;

/**
 * Parses a disk image name into its group key and version.
 * Returns { groupKey, version, displayName } where version is empty for
 * images without a version suffix.
 */
export function parseDiskImageName(name: string): {
  groupKey: string;
  version: string;
  displayName: string;
} {
  const match = name.match(VERSION_REGEX);
  const groupKey = match ? match[1] : name;
  const version = match ? match[2] : '';
  const displayName = groupKey.charAt(0).toUpperCase() + groupKey.slice(1);
  return { groupKey, version, displayName };
}

/**
 * Groups public disk images by their base name, splitting on the first hyphen
 * followed by a digit to extract the version suffix.
 *
 * Single-version images (e.g., "ubuntu") get a group with one entry.
 * Multi-version images (e.g., "python-3.11", "python-3.12") are grouped together.
 */
export function groupPublicDiskImages(
  images: { name: string }[]
): Record<string, { label: string; versions: DiskImageVersion[] }> {
  const groups: Record<string, { label: string; versions: DiskImageVersion[] }> = {};
  for (const img of images) {
    if (!img.name) continue;
    const { groupKey, version, displayName } = parseDiskImageName(img.name);
    if (!groups[groupKey]) {
      groups[groupKey] = { label: displayName, versions: [] };
    }
    groups[groupKey].versions.push({
      value: img.name,
      label: version || img.name,
    });
  }
  return groups;
}

/**
 * Converts grouped disk images to select options, showing version count for multi-version groups.
 */
export function groupsToSelectOptions(
  groups: Record<string, { label: string; versions: DiskImageVersion[] }>
): { value: string; label: string }[] {
  return Object.entries(groups).map(([key, group]) => ({
    value: key,
    label:
      group.versions.length > 1
        ? `${group.label} (${group.versions.length} versions)`
        : group.label,
  }));
}
